import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { B, FONT, LOGO_FALLBACK } from '../lib/constants';

export default function Login({ inviteToken, inviteProject }) {
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteData, setInviteData] = useState(null);

  useEffect(() => {
    if (inviteToken) {
      setMode('signup');
      loadInvite();
    }
  }, [inviteToken]);

  const loadInvite = async () => {
    const { data } = await supabase
      .from('client_invitations')
      .select('*')
      .eq('token', inviteToken)
      .maybeSingle();
    if (data) {
      setInviteData(data);
      setEmail(data.email);
    } else {
      setError('This invite link is invalid or has already been used.');
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError('Incorrect email or password.');
    setLoading(false);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: { emailRedirectTo: window.location.origin },
      });
      if (signUpError) throw signUpError;

      // Set up profile immediately — works even if email confirm is required
      const userId = data.user?.id;
      if (userId && inviteData) {
        const role = inviteData.project_id ? 'client' : 'assistant';
        await supabase.from('user_profiles').upsert({
          id: userId,
          email: email.trim().toLowerCase(),
          role,
          project_id: inviteData.project_id || null,
        });
        await supabase.from('client_invitations').update({ accepted: true }).eq('token', inviteToken);
      }

      // Try signing in immediately (works if email confirm is off)
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (signInError) {
        // Email confirmation is required
        setSuccess('Account created! Check your email for a confirmation link, then come back and sign in.');
        setMode('signin');
      }
      // If sign in succeeded, app will redirect automatically
    } catch(e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const inp = {
    background: B.slate, border: `1px solid ${B.border}`, borderRadius: 10,
    padding: '13px 16px', color: B.white, fontSize: 15, outline: 'none',
    fontFamily: FONT, width: '100%', boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', background: B.black, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: FONT }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={LOGO_FALLBACK} alt="Muller Homes" style={{ height: 60, marginBottom: 16 }} onError={e => e.target.style.display = 'none'} />
          <div style={{ color: B.goldlt, fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700 }}>Muller Homes</div>
          <div style={{ color: B.muted, fontSize: 12, marginTop: 4 }}>
            {inviteData ? `You're invited to view your build portal` : 'Build Manager'}
          </div>
        </div>

        {!inviteToken && (
          <div style={{ display: 'flex', background: B.slate, borderRadius: 10, padding: 3, marginBottom: 24 }}>
            {['signin', 'signup'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); setSuccess(''); }}
                style={{ flex: 1, background: mode === m ? B.goldlt : 'transparent', color: mode === m ? B.black : B.muted, border: 'none', borderRadius: 8, padding: '10px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: FONT }}>
                {m === 'signin' ? 'Sign In' : 'Sign Up'}
              </button>
            ))}
          </div>
        )}

        {success && (
          <div style={{ background: '#0D2D1A', border: '1px solid #4CAF7D', borderRadius: 8, padding: '10px 14px', color: '#4CAF7D', fontSize: 13, marginBottom: 16 }}>
            {success}
          </div>
        )}

        <form onSubmit={mode === 'signin' ? handleSignIn : handleSignUp}>
          <input type="email" placeholder="Email address" value={email}
            onChange={e => setEmail(e.target.value)} style={{ ...inp, marginBottom: 12 }}
            autoComplete="email" required readOnly={!!inviteData} />

          {/* Password with show/hide toggle */}
          <div style={{ position: 'relative', marginBottom: 20 }}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={mode === 'signup' ? 'Create a password' : 'Password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ ...inp, paddingRight: 48 }}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              required
            />
            <button type="button" onClick={() => setShowPassword(v => !v)}
              style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: B.muted, fontSize: 18, lineHeight: 1, padding: 0 }}>
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>

          {error && (
            <div style={{ background: '#2D1010', border: '1px solid #E74C3C', borderRadius: 8, padding: '10px 14px', color: '#E74C3C', fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading}
            style={{ width: '100%', background: loading ? B.muted : B.goldlt, color: B.black, border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, fontFamily: FONT, cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? (mode === 'signin' ? 'Signing in…' : 'Creating account…') : (mode === 'signin' ? 'Sign In' : 'Create Account & View My Build')}
          </button>
        </form>

        {!inviteToken && (
          <div style={{ color: B.muted, fontSize: 12, textAlign: 'center', marginTop: 16 }}>
            {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
            <span onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); setSuccess(''); }}
              style={{ color: B.goldlt, cursor: 'pointer', textDecoration: 'underline' }}>
              {mode === 'signin' ? 'Sign up' : 'Sign in'}
            </span>
          </div>
        )}

        <div style={{ color: B.muted, fontSize: 11, textAlign: 'center', marginTop: 24 }}>Muller Homes LLC · Columbia, TN</div>
      </div>
    </div>
  );
}
